import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, downloadFile, downloadFilePost } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, refreshCounts } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import {
  ScanSearch, RefreshCw, Trash2, X, Download, Package, Truck, ExternalLink,
  Search, Sparkles, Languages, Image as ImageIcon, Calculator, Wand2,
  ChevronDown, ChevronUp, Rows2, Rows3, Plus, Loader2, Copy, ShieldCheck,
  AlertTriangle, Type, Upload as Upload2, Filter, Check,
} from "lucide-react";
import { useAuth, usePerms } from "@/lib/auth";
import ImageThumb from "@/components/ImageThumb";

const MARKETPLACES = ["amazon", "flipkart", "myntra", "shopify"];

const ALL_COLUMNS = [
  { key: "image",       label: "",         width: 50,  compact: true,  align: "center" },
  { key: "offer_id",    label: "Offer ID", width: 130, compact: true,  mono: true },
  { key: "title",       label: "Title",    width: 320, compact: true },
  { key: "seller",      label: "Seller",   width: 140, compact: false },
  { key: "cny",         label: "CNY",      width: 80,  compact: true,  mono: true, align: "right" },
  { key: "inr",         label: "Final ₹",  width: 90,  compact: true,  mono: true, align: "right" },
  { key: "mrp",         label: "MRP ₹",    width: 80,  compact: false, mono: true, align: "right" },
  { key: "moq",         label: "MOQ",      width: 60,  compact: false, mono: true, align: "right" },
  { key: "images",      label: "Src Img",  width: 60,  compact: false, mono: true, align: "right" },
  { key: "generated",   label: "Gen Img",  width: 70,  compact: true,  align: "center" },
  { key: "status",      label: "Status",   width: 75,  compact: true,  align: "center" },
  { key: "is_listed",   label: "Listed",   width: 60,  compact: true,  align: "center" },
  { key: "workflow_status", label: "Workflow", width: 120, compact: true, align: "center" },
  { key: "video_url",   label: "Video",    width: 60,  compact: false, align: "center" },
  { key: "category",    label: "Category", width: 160, compact: false },
  { key: "browse_node", label: "Browse",   width: 110, compact: false, mono: true },
  { key: "scraped_by",  label: "Scraped by", width: 130, compact: false },
  { key: "scraped_at",  label: "Scraped",  width: 90,  compact: false, mono: true },
  { key: "listed_by",   label: "Listed by", width: 130, compact: false },
  { key: "listed_at",   label: "Listed on",width: 90,  compact: false, mono: true },
  { key: "comments",    label: "Comments", width: 200, compact: false },
  { key: "updated",     label: "Updated",  width: 90,  compact: false, mono: true },
];

function StatusBadge({ status }) {
  const map = {
    ok:      { bg: "bg-emerald-50", tx: "text-emerald-800", bd: "border-emerald-200", label: "OK" },
    partial: { bg: "bg-amber-50",   tx: "text-amber-900",   bd: "border-amber-300",   label: "Partial" },
    failed:  { bg: "bg-red-50",     tx: "text-red-800",     bd: "border-red-200",     label: "Blocked" },
  };
  const t = map[status] || map.partial;
  return (
    <span data-testid={`status-badge-${status || "unknown"}`}
          className={`inline-flex items-center h-4 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${t.bg} ${t.tx} ${t.bd}`}>
      {t.label}
    </span>
  );
}

function ago(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function getCell(r, k) {
  const p = r.pricing || {};
  switch (k) {
    case "offer_id": return r.offer_id || "";
    case "title":    return r.title_en || r.title_cn || "";
    case "seller":   return r.seller || "";
    case "cny":      return r.price_min_cny ?? "";
    case "inr":      return p.final_inr ?? "";
    case "mrp":      return p.mrp_inr ?? "";
    case "moq":      return r.moq ?? "";
    case "images":   return (r.images || []).length;
    case "generated":return Object.keys(r.generated_images || {}).length;
    case "status":   return r.scrape_status || "";
    case "is_listed":return r.is_listed ? 1 : 0;
    case "workflow_status": return r.workflow_status || "";
    case "video_url": return r.video_url ? 1 : 0;
    case "category": return Array.isArray(r.category) ? r.category.join(" > ") : (r.category || "");
    case "browse_node": return r.browse_node || "";
    case "scraped_by": return r.scraped_by_name || r.scraped_by_email || "";
    case "scraped_at": return r.scraped_at || "";
    case "listed_by":  return r.listed_by_name || r.listed_by_email || "";
    case "listed_at":  return r.listed_at || "";
    case "comments":   return r.comments || "";
    case "updated":  return r.updated_at || "";
    default: return "";
  }
}

export default function Sourcing1688() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [url, setUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [translate, setTranslate] = useState(true);
  const [genImages, setGenImages] = useState(false);
  const [detail, setDetail] = useState(null);
  const [wide, setWide] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [sort, setSort] = useState({ key: "updated", dir: "desc" });
  const [filter, setFilter] = useState({
    scraped_by: "", listed_by: "",
    scraped_from: "", scraped_to: "",
    listed_from: "", listed_to: "",
    workflow_status: "",
    product_type: "", product_category: "",
    unit_cost_min: "", unit_cost_max: "",
    shipping_min: "", shipping_max: "",
    moq_min: "", moq_max: "",
    sp_min: "", sp_max: "",
    weight_min: "", weight_max: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [amazonCategoriesList, setAmazonCategoriesList] = useState([]);
  const { canEdit } = usePerms();
  const { user } = useAuth();
  const editable = canEdit("sourcing1688") || canEdit("listings") || user?.role === "admin";

  const load = async () => {
    try {
      const params = { ...(q ? { q } : {}) };
      Object.entries(filter).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      const { data } = await api.get("/1688", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setUsers(Array.isArray(data) ? data : []);
      } catch (_e) { /* silent */ }
      try {
        const { data } = await api.get("/1688/meta/amazon-categories");
        setAmazonCategoriesList(data?.categories || []);
      } catch (_e) { /* silent */ }
    })();
  }, []);
  const clearFilters = () => {
    setFilter({
      scraped_by: "", listed_by: "",
      scraped_from: "", scraped_to: "",
      listed_from: "", listed_to: "",
      workflow_status: "",
      product_type: "", product_category: "",
      unit_cost_min: "", unit_cost_max: "",
      shipping_min: "", shipping_max: "",
      moq_min: "", moq_max: "",
      sp_min: "", sp_max: "",
      weight_min: "", weight_max: "",
    });
    setTimeout(load, 50);
  };
  const activeFilterCount = Object.values(filter).filter((v) => v !== "" && v != null).length;

  // Poll open detail if an image job is running so progress reflects in UI
  useEffect(() => {
    if (!detail?.id || detail.image_job_status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/1688/${detail.id}`);
        setDetail(data);
        if (data.image_job_status !== "running") {
          load();
          refreshCounts();
        }
      } catch (_e) { /* silent */ }
    }, 5000);
    return () => clearInterval(t);
  }, [detail?.id, detail?.image_job_status]);

  const doScrape = async () => {
    if (!url.trim()) return;
    setScraping(true);
    try {
      const { data } = await api.post("/1688/scrape", {
        url_or_id: url.trim(), save: true, translate, generate_images: genImages,
      });
      if (data.scrape_status === "failed") {
        toast.warning("1688 blocked the scrape. You can manually fill in fields.");
      } else if (data.scrape_status === "partial") {
        toast.warning("Partial data scraped — some fields could not be extracted.");
      } else {
        toast.success(`Scraped ${data.title_cn || data.offer_id}`);
      }
      setUrl("");
      setDetail(data);
      load(); refreshCounts();
    } catch (e) { toast.error(formatApiError(e)); }
    setScraping(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this 1688 product?")) return;
    try {
      await api.delete(`/1688/${id}`);
      toast.success("Deleted");
      setDetail(null); load(); refreshCounts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/1688/${r.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visibleColumns = useMemo(
    () => (wide ? ALL_COLUMNS : ALL_COLUMNS.filter((c) => c.compact)),
    [wide]
  );

  const filteredRows = useMemo(() => {
    const gs = q.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (!gs) return true;
      const hay = [r.title_cn, r.title_en, r.offer_id, r.seller].join(" ").toLowerCase();
      return hay.includes(gs);
    });
    out.sort((a, b) => {
      const av = getCell(a, sort.key), bv = getCell(b, sort.key);
      const an = typeof av === "number" ? av : parseFloat(av);
      const bn = typeof bv === "number" ? bv : parseFloat(bv);
      const cmp = (!isNaN(an) && !isNaN(bn) && av !== "" && bv !== "")
        ? an - bn
        : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, sort]);

  const setSortKey = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filteredRows.forEach((r) => next.delete(r.id));
    else filteredRows.forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulkExport = async (m) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await downloadFilePost(`/1688/export/${m}.csv`, { ids }, `1688-${m}-${ids.length}.csv`);
      toast.success(`Exported ${ids.length} to ${m}`);
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  return (
    <>
      <PageHeader
        title="1688 Sourcing"
        subtitle="Scrape 1688.com • Translate CN → EN • Generate images (FREE) • Price for India • Export"
        testId="module-title-sourcing1688"
        module="sourcing1688"
      >
        <div className="flex items-center gap-1.5">
          <button
            data-testid="1688-toggle-filters"
            onClick={() => setShowFilters((s) => !s)}
            className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${showFilters || activeFilterCount > 0 ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <Filter size={12} /> Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
          </button>
          <button
            data-testid="1688-toggle-wide"
            onClick={() => setWide((w) => !w)}
            className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${wide ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            {wide ? <Rows3 size={12} /> : <Rows2 size={12} />} {wide ? "Wide" : "Compact"}
          </button>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="1688-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search title / offer ID…"
              className="h-8 pl-7 pr-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </PageHeader>

      <PageBody>
        {/* Filter panel */}
        {showFilters && (
          <div className="mb-3 bg-blue-50/40 border border-blue-200 rounded-sm p-2.5" data-testid="1688-filter-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold inline-flex items-center gap-1">
                <Filter size={11} /> Filters
              </div>
              <div className="flex items-center gap-3">
                <button onClick={clearFilters} data-testid="1688-clear-filters" className="text-[11px] text-slate-600 hover:text-red-600 inline-flex items-center gap-0.5">
                  <X size={10} /> clear all
                </button>
                <button onClick={load} data-testid="1688-apply-filters" className="h-7 px-3 inline-flex items-center gap-1 bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 rounded-sm text-[11.5px] font-semibold">
                  <Check size={11} /> Apply
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <FilterUserSelect label="Scraped by" value={filter.scraped_by} onChange={(v) => setFilter({ ...filter, scraped_by: v })} users={users} testId="1688-filter-scraped-by" />
              <FilterUserSelect label="Listed by" value={filter.listed_by} onChange={(v) => setFilter({ ...filter, listed_by: v })} users={users} testId="1688-filter-listed-by" />
              <FilterRange label="Scraped date" type="date" min={filter.scraped_from} max={filter.scraped_to}
                           onChangeMin={(v) => setFilter({ ...filter, scraped_from: v })}
                           onChangeMax={(v) => setFilter({ ...filter, scraped_to: v })}
                           testId="1688-filter-scraped-date" />
              <FilterRange label="Listed date" type="date" min={filter.listed_from} max={filter.listed_to}
                           onChangeMin={(v) => setFilter({ ...filter, listed_from: v })}
                           onChangeMax={(v) => setFilter({ ...filter, listed_to: v })}
                           testId="1688-filter-listed-date" />
              <FilterSelect label="Workflow status" value={filter.workflow_status} onChange={(v) => setFilter({ ...filter, workflow_status: v })} testId="1688-filter-workflow-status"
                options={[["", "Any"], ["pending_pricing", "Pending for Pricing"], ["ready_for_listing", "Ready for Listing"]]} />
              <FilterInput label="Product type" value={filter.product_type} onChange={(v) => setFilter({ ...filter, product_type: v })} testId="1688-filter-product-type" />
              <FilterSelect label="Product category" value={filter.product_category} onChange={(v) => setFilter({ ...filter, product_category: v })} testId="1688-filter-category"
                options={[["", "Any"], ["Electrical", "Electrical"], ["Non-Electrical", "Non-Electrical"]]} />
              <FilterRange label="Unit cost (¥)" min={filter.unit_cost_min} max={filter.unit_cost_max}
                           onChangeMin={(v) => setFilter({ ...filter, unit_cost_min: v })}
                           onChangeMax={(v) => setFilter({ ...filter, unit_cost_max: v })}
                           testId="1688-filter-unit-cost" />
              <FilterRange label="Shipping (₹)" min={filter.shipping_min} max={filter.shipping_max}
                           onChangeMin={(v) => setFilter({ ...filter, shipping_min: v })}
                           onChangeMax={(v) => setFilter({ ...filter, shipping_max: v })}
                           testId="1688-filter-shipping" />
              <FilterRange label="MOQ" min={filter.moq_min} max={filter.moq_max}
                           onChangeMin={(v) => setFilter({ ...filter, moq_min: v })}
                           onChangeMax={(v) => setFilter({ ...filter, moq_max: v })}
                           testId="1688-filter-moq" />
              <FilterRange label="SP (₹)" hint="Calculated Selling Price" min={filter.sp_min} max={filter.sp_max}
                           onChangeMin={(v) => setFilter({ ...filter, sp_min: v })}
                           onChangeMax={(v) => setFilter({ ...filter, sp_max: v })}
                           testId="1688-filter-sp" />
              <FilterRange label="Weight (g)" min={filter.weight_min} max={filter.weight_max}
                           onChangeMin={(v) => setFilter({ ...filter, weight_min: v })}
                           onChangeMax={(v) => setFilter({ ...filter, weight_max: v })}
                           testId="1688-filter-weight" />
            </div>
          </div>
        )}

        {/* Scraper input strip */}
        {editable && (
          <div className="mb-3 bg-white border border-slate-200 rounded-sm p-2.5 flex flex-wrap gap-2 items-center">
            <ScanSearch size={15} className="text-rose-600" />
            <span className="text-[12px] font-semibold text-slate-700">Scrape 1688:</span>
            <input
              data-testid="1688-scrape-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doScrape()}
              placeholder="Paste 1688 URL (e.g. https://detail.1688.com/offer/838823424938.html)"
              className="flex-1 min-w-[280px] h-8 px-2 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
            />
            <label className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-700 select-none px-2 h-8 border border-slate-200 rounded-sm bg-slate-50 cursor-pointer">
              <input type="checkbox" data-testid="1688-translate" checked={translate}
                     onChange={(e) => setTranslate(e.target.checked)} className="accent-rose-600" />
              <Languages size={12} /> Translate
            </label>
            <label className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-700 select-none px-2 h-8 border border-slate-200 rounded-sm bg-slate-50 cursor-pointer" title="Runs in background — expect ~30-60s">
              <input type="checkbox" data-testid="1688-gen-images" checked={genImages}
                     onChange={(e) => setGenImages(e.target.checked)} className="accent-rose-600" />
              <Sparkles size={12} /> Generate images
            </label>
            <Button variant="primary" onClick={doScrape} disabled={scraping || !url.trim()} testId="1688-scrape-btn">
              {scraping ? <><RefreshCw size={13} className="animate-spin" /> Scraping…</> : <><ScanSearch size={13} /> Scrape</>}
            </Button>
          </div>
        )}

        {/* Data grid */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="erp-table w-full" data-testid="1688-grid">
              <thead className="sticky top-0 z-10 bg-slate-100">
                <tr>
                  <th style={{ width: 32 }} className="text-center">
                    <input type="checkbox" data-testid="1688-select-all" checked={allSelected}
                           onChange={toggleAll} className="accent-rose-600" />
                  </th>
                  {visibleColumns.map((c) => (
                    <th key={c.key}
                        style={{ width: c.width, minWidth: c.width, textAlign: c.align || "left" }}
                        className="cursor-pointer select-none hover:bg-slate-200"
                        onClick={() => c.key !== "image" && setSortKey(c.key)}
                        data-testid={`1688-col-header-${c.key}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {c.label}
                        {sort.key === c.key && c.key !== "image" && (
                          sort.dir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const rowSelected = selected.has(r.id);
                  const genCount = Object.keys(r.generated_images || {}).length;
                  const firstImg = (r.generated_images?.master?.storage_path
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/1688/${r.id}/images/master`
                    : (r.images || [])[0]);
                  return (
                    <tr key={r.id}
                        data-testid={`1688-row-${r.id}`}
                        className={`border-t border-slate-100 hover:bg-rose-50/40 ${rowSelected ? "bg-blue-50/60" : ""}`}
                        onClick={() => openDetail(r)}>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`1688-row-select-${r.id}`}
                               checked={rowSelected} onChange={() => toggleOne(r.id)}
                               className="accent-rose-600" />
                      </td>
                      {visibleColumns.map((c) => {
                        const align = c.align || "left";
                        const mono = c.mono ? "font-mono" : "";
                        const common = `text-[12px] ${mono}`;
                        switch (c.key) {
                          case "image":
                            return (
                              <td key={c.key} className="p-1">
                                {firstImg
                                  ? <ImageThumb src={firstImg} size={64} fit="contain" testId={`sourcing-thumb-${r.id}`} />
                                  : <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-sm flex items-center justify-center text-slate-300"><ImageIcon size={14} /></div>}
                              </td>
                            );
                          case "offer_id":
                            return <td key={c.key} className={common}>{r.offer_id}</td>;
                          case "title":
                            return (
                              <td key={c.key} className="text-[12px] font-medium text-slate-800 truncate max-w-[340px]" title={r.title_en || r.title_cn || ""}>
                                {r.title_en || r.title_cn || <span className="text-slate-400 italic">Untitled — needs manual entry</span>}
                              </td>
                            );
                          case "seller":
                            return <td key={c.key} className={common}>{r.seller || "—"}</td>;
                          case "cny":
                            return <td key={c.key} className={`${common} text-right text-amber-800`}>{r.price_min_cny != null ? `¥${r.price_min_cny}` : "—"}</td>;
                          case "inr":
                            return <td key={c.key} className={`${common} text-right font-semibold text-emerald-800`}>{r.pricing?.final_inr != null ? `₹${r.pricing.final_inr}` : "—"}</td>;
                          case "mrp":
                            return <td key={c.key} className={`${common} text-right text-slate-500`}>{r.pricing?.mrp_inr != null ? `₹${r.pricing.mrp_inr}` : "—"}</td>;
                          case "moq":
                            return <td key={c.key} className={`${common} text-right text-slate-600`}>{r.moq ?? "—"}</td>;
                          case "images":
                            return <td key={c.key} className={`${common} text-right text-slate-600`}>{(r.images || []).length}</td>;
                          case "generated":
                            return (
                              <td key={c.key} className="text-center">
                                {r.image_job_status === "running"
                                  ? <span className="inline-flex items-center gap-1 text-rose-600 text-[11px]"><Loader2 size={10} className="animate-spin" /> Gen…</span>
                                  : genCount > 0
                                    ? <span className="inline-flex items-center gap-0.5 text-rose-700 font-mono text-[11px]"><Sparkles size={10} />{genCount}</span>
                                    : <span className="text-slate-300">—</span>}
                              </td>
                            );
                          case "status":
                            return <td key={c.key} className="text-center"><StatusBadge status={r.scrape_status} /></td>;
                          case "is_listed":
                            return <td key={c.key} className="text-center">
                              {r.is_listed
                                ? <span className="inline-flex items-center gap-0.5 h-4 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200"><Check size={9} /> Yes</span>
                                : <span className="inline-flex items-center h-4 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-slate-50 text-slate-500 border border-slate-200">No</span>}
                            </td>;
                          case "workflow_status": {
                            const ws = r.workflow_status || "pending_pricing";
                            const style = ws === "ready_for_listing"
                              ? "bg-blue-50 text-blue-800 border-blue-200"
                              : "bg-amber-50 text-amber-900 border-amber-300";
                            const label = ws === "ready_for_listing" ? "Ready" : "Pending $";
                            return <td key={c.key} className="text-center">
                              <span className={`inline-flex items-center h-4 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${style}`} title={ws === "ready_for_listing" ? "Ready for Listing" : "Pending for Pricing"}>
                                {label}
                              </span>
                            </td>;
                          }
                          case "video_url":
                            return <td key={c.key} className="text-center">
                              {r.video_url
                                ? <a href={r.video_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline inline-flex items-center gap-0.5 text-[11px]" title={r.video_url}><ExternalLink size={10} /> View</a>
                                : <span className="text-slate-300">—</span>}
                            </td>;
                          case "category":
                            return <td key={c.key} className="text-[11.5px] text-slate-700 truncate max-w-[160px]" title={getCell(r, c.key)}>{getCell(r, c.key) || "—"}</td>;
                          case "browse_node":
                            return <td key={c.key} className={common}>{r.browse_node || "—"}</td>;
                          case "scraped_by":
                            return <td key={c.key} className="text-[11.5px] text-slate-700 truncate max-w-[130px]" title={getCell(r, c.key)}>{getCell(r, c.key) || "—"}</td>;
                          case "scraped_at":
                            return <td key={c.key} className="text-[11px] font-mono text-slate-500">{r.scraped_at ? ago(r.scraped_at) : "—"}</td>;
                          case "listed_by":
                            return <td key={c.key} className="text-[11.5px] text-slate-700 truncate max-w-[130px]" title={getCell(r, c.key)}>{getCell(r, c.key) || "—"}</td>;
                          case "listed_at":
                            return <td key={c.key} className="text-[11px] font-mono text-slate-500">{r.listed_at ? ago(r.listed_at) : "—"}</td>;
                          case "comments":
                            return <td key={c.key} className="text-[11.5px] text-slate-700 truncate max-w-[200px]" title={r.comments || ""}>{r.comments || <span className="text-slate-300">—</span>}</td>;
                          case "updated":
                            return <td key={c.key} className="text-[11px] font-mono text-slate-500">{ago(r.updated_at)}</td>;
                          default:
                            return <td key={c.key} />;
                        }
                      })}
                    </tr>
                  );
                })}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="p-10 text-center text-slate-500 text-[13px] border-t border-slate-100">
                      {rows.length === 0
                        ? "No products yet. Paste a 1688 URL above to source your first product."
                        : "No rows match your search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer / bulk actions */}
        <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-600">
          <div className="font-mono" data-testid="1688-row-count">
            {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"}
            {selected.size > 0 && <span className="ml-2 text-rose-700 font-semibold">• {selected.size} selected</span>}
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="1688-bulk-action-bar">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Bulk export:</span>
              {MARKETPLACES.map((m) => (
                <button key={m} onClick={() => bulkExport(m)} data-testid={`1688-bulk-export-${m}`}
                        className="h-7 px-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-sm text-[11.5px] font-semibold capitalize">
                  <Download size={11} /> {m}
                </button>
              ))}
              <button onClick={() => setSelected(new Set())} className="h-7 px-2 inline-flex items-center gap-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 rounded-sm text-[11.5px]">
                <X size={11} /> Clear
              </button>
            </div>
          )}
        </div>
      </PageBody>

      {detail && (
        <DetailDrawer
          rid={detail.id}
          detail={detail}
          onClose={() => setDetail(null)}
          onChange={(d) => setDetail(d)}
          onRefresh={load}
          onDelete={() => remove(detail.id)}
          editable={editable}
          users={users}
        />
      )}
    </>
  );
}

// ------------------ Detail drawer -----------------------------------------

function DetailDrawer({ rid, detail, onClose, onChange, onRefresh, onDelete, editable, users = [] }) {
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [pricingDraft, setPricingDraft] = useState(() => ({
    ...(detail.pricing?.knobs || {}),
  }));

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/1688/${rid}`, payload);
      onChange(data);
      toast.success("Saved");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const regen = async (payload) => {
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/1688/${rid}/regenerate`, payload);
      onChange(data);
      if (payload.generate_images) toast.info("Image job started — this takes ~30-60s.");
      else toast.success("Regenerated");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setRegenLoading(false);
  };

  const downloadOne = (m) => downloadFile(`/1688/${rid}/export/${m}.csv`, `1688-${detail.offer_id}-${m}.csv`);

  const importToProc = async () => {
    try {
      await api.post(`/1688/${rid}/import-to-procurement`);
      toast.success("Added to Procurement");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const gi = detail.generated_images || {};
  const imgUrl = (slot) => `${process.env.REACT_APP_BACKEND_URL}/api/1688/${rid}/images/${slot}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div className="w-[960px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <StatusBadge status={detail.scrape_status} />
            <div className="min-w-0">
              <div className="font-heading text-[14px] font-semibold text-slate-900 truncate" title={detail.title_en || detail.title_cn}>
                {detail.title_en || detail.title_cn || <span className="text-slate-400 italic">Untitled — please fill in manually</span>}
              </div>
              <div className="font-mono text-[11px] text-slate-500">
                {detail.offer_id}
                <a href={detail.url} target="_blank" rel="noreferrer" className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-0.5">
                  1688 <ExternalLink size={9} />
                </a>
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="1688-detail-close" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
        </div>

        {/* Action bar */}
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 flex flex-wrap items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Export:</span>
          {MARKETPLACES.map((m) => (
            <button key={m} onClick={() => downloadOne(m)} data-testid={`1688-download-${m}`}
                    className="h-7 px-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-sm text-[11.5px] font-semibold capitalize">
              <Download size={11} /> {m}
            </button>
          ))}
          <div className="mx-2 h-5 border-l border-slate-200" />
          <Button onClick={importToProc} testId="1688-import-procurement">
            <Truck size={12} /> Add to Procurement
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {editable && (
              <button onClick={() => regen({ translate: true })} disabled={regenLoading} data-testid="1688-re-translate"
                      className="text-[11.5px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                <Languages size={11} /> Re-translate
              </button>
            )}
            {editable && (
              <button onClick={() => regen({ generate_images: true })}
                      disabled={regenLoading || detail.image_job_status === "running" || !(detail.images || []).length}
                      data-testid="1688-gen-images-btn"
                      className="text-[11.5px] text-rose-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                {detail.image_job_status === "running"
                  ? <><Loader2 size={11} className="animate-spin" /> Generating…</>
                  : <><Wand2 size={11} /> Generate images</>}
              </button>
            )}
            {editable && (
              <button onClick={onDelete} data-testid="1688-delete"
                      className="text-[11.5px] text-red-600 hover:underline inline-flex items-center gap-1">
                <Trash2 size={11} /> Delete
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-white px-4 flex items-center gap-4 shrink-0">
          {["overview", "images", "variants", "amazon", "pricing", "specs", "export"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`1688-tab-${t}`}
                    className={`h-9 text-[12px] font-semibold border-b-2 capitalize ${tab === t ? "border-rose-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              {t === "amazon" ? "Amazon Listing" : t}{t === "variants" && (detail.variants || []).length > 0 ? ` (${detail.variants.length})` : ""}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {detail.scrape_error && tab === "overview" && (
            <div className="m-4 p-3 border border-amber-300 bg-amber-50 rounded-sm text-[12px] text-amber-900">
              <strong>Scraper note:</strong> {detail.scrape_error}
            </div>
          )}
          {detail.image_job_status === "running" && (
            <div className="m-4 p-3 border border-rose-300 bg-rose-50 rounded-sm text-[12px] text-rose-800 inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              <span>Image generation in progress. Master (rembg) + 6 creative images via Pollinations…</span>
            </div>
          )}
          {detail.image_job_status === "failed" && (
            <div className="m-4 p-3 border border-red-300 bg-red-50 rounded-sm text-[12px] text-red-800">
              <strong>Image job failed:</strong> {detail.image_job_error}
            </div>
          )}

          {tab === "overview" && (
            <OverviewTab detail={detail} onSave={patch} saving={saving} editable={editable} users={users} />
          )}
          {tab === "images" && (
            <ImagesTab detail={detail} imgUrl={imgUrl} />
          )}
          {tab === "variants" && (
            <VariantsTab rid={rid} detail={detail} onChange={onChange} onRefresh={onRefresh} editable={editable} />
          )}
          {tab === "amazon" && (
            <AmazonListingTab rid={rid} detail={detail} onChange={onChange} onRefresh={onRefresh} editable={editable} />
          )}
          {tab === "pricing" && (
            <PricingTab draft={pricingDraft} setDraft={setPricingDraft}
                        detail={detail} onApply={(k) => regen({ pricing: k })}
                        regenLoading={regenLoading} editable={editable} />
          )}
          {tab === "specs" && (
            <SpecsTab detail={detail} onSave={patch} editable={editable} />
          )}
          {tab === "export" && (
            <ExportTab detail={detail} rid={rid} />
          )}
        </div>
      </div>
    </div>
  );
}

// ------- Tabs -------------------------------------------------------------

function OverviewTab({ detail, onSave, saving, editable, users = [] }) {
  const [titleCn, setTitleCn] = useState(detail.title_cn || "");
  const [titleEn, setTitleEn] = useState(detail.title_en || "");
  const [priceMin, setPriceMin] = useState(detail.price_min_cny ?? "");
  const [shippingCny, setShippingCny] = useState(detail.shipping_cny ?? "");
  const [moq, setMoq] = useState(detail.moq ?? "");
  const [seller, setSeller] = useState(detail.seller || "");
  const [descCn, setDescCn] = useState(detail.description_cn || "");
  const [descEn, setDescEn] = useState(detail.description_en || "");
  const [imgsRaw, setImgsRaw] = useState((detail.images || []).join("\n"));
  const [productCategory, setProductCategory] = useState(detail.product_category || "");
  const [variationType, setVariationType] = useState(detail.variation_type || "");
  const [categoryPath, setCategoryPath] = useState(Array.isArray(detail.category) ? detail.category.join(" > ") : (detail.category || ""));
  const [browseNode, setBrowseNode] = useState(detail.browse_node || "");
  const [comments, setComments] = useState(detail.comments || "");
  const [isListed, setIsListed] = useState(!!detail.is_listed);
  const [scrapedBy, setScrapedBy] = useState(detail.scraped_by || "");
  const [listedBy, setListedBy] = useState(detail.listed_by || "");
  const [scrapedAt, setScrapedAt] = useState(
    detail.scraped_at ? new Date(detail.scraped_at).toISOString().slice(0, 16) : ""
  );
  const [workflowStatus, setWorkflowStatus] = useState(detail.workflow_status || "pending_pricing");
  const [videoUrl, setVideoUrl] = useState(detail.video_url || "");
  const [productType, setProductType] = useState(detail.product_type || "");
  const [brandName, setBrandName] = useState(detail.brand_name || "");
  const [amazonCategory, setAmazonCategory] = useState(detail.amazon_category || "");
  const [resellerMarginPct, setResellerMarginPct] = useState(detail.reseller_margin_percent ?? "");
  const [amazonCategories, setAmazonCategories] = useState([]);
  useEffect(() => {
    api.get("/1688/meta/amazon-categories").then((r) => setAmazonCategories(r.data?.categories || [])).catch(() => {});
  }, []);

  const save = async () => {
    const parsed = imgsRaw.split("\n").map((s) => s.trim()).filter(Boolean);
    // Save meta (product_category / variation_type) alongside standard patch
    if (productCategory !== (detail.product_category || "") || variationType !== (detail.variation_type || "")) {
      try {
        await api.patch(`/1688/${detail.id}/meta`, {
          ...(productCategory ? { product_category: productCategory } : {}),
          ...(variationType ? { variation_type: variationType } : {}),
        });
      } catch (e) { toast.error(formatApiError(e)); }
    }
    onSave({
      title_cn: titleCn, title_en: titleEn,
      price_min_cny: priceMin === "" ? null : parseFloat(priceMin),
      shipping_cny: shippingCny === "" ? null : parseFloat(shippingCny),
      moq: moq === "" ? null : parseInt(moq, 10),
      seller,
      description_cn: descCn, description_en: descEn,
      images: parsed,
      category: categoryPath ? categoryPath.split(">").map((c) => c.trim()).filter(Boolean) : [],
      browse_node: browseNode || null,
      comments,
      is_listed: isListed,
      scraped_by: scrapedBy || null,
      listed_by: listedBy || null,
      scraped_at: scrapedAt ? new Date(scrapedAt).toISOString() : null,
      workflow_status: workflowStatus || "pending_pricing",
      video_url: videoUrl || "",
      product_type: productType || "",
      brand_name: brandName || "",
      amazon_category: amazonCategory || "",
      reseller_margin_percent: resellerMarginPct === "" ? 0 : parseFloat(resellerMarginPct),
    });
  };

  return (
    <div className="p-4 space-y-3">
      <Field label="Chinese title" testId="1688-field-title-cn">
        <textarea rows={2} value={titleCn} onChange={(e) => setTitleCn(e.target.value)}
                  disabled={!editable} className="w-full text-[13px] border border-slate-200 rounded-sm p-2 focus:ring-1 focus:ring-rose-500 outline-none font-medium" />
      </Field>
      <Field label="English title" testId="1688-field-title-en">
        <textarea rows={2} value={titleEn} onChange={(e) => setTitleEn(e.target.value)}
                  disabled={!editable} className="w-full text-[13px] border border-slate-200 rounded-sm p-2 focus:ring-1 focus:ring-rose-500 outline-none" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Product category" hint="Electrical products require voltage / battery / safety cert on marketplaces" testId="1688-field-category">
          <select value={productCategory} onChange={(e) => setProductCategory(e.target.value)}
                  disabled={!editable} data-testid="1688-select-category"
                  className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none bg-white">
            <option value="">— pick one —</option>
            <option value="Electrical">Electrical</option>
            <option value="Non-Electrical">Non-Electrical</option>
          </select>
        </Field>
        <Field label={`Variation type${detail.variants?.length ? ` (${detail.variants.length} variant${detail.variants.length === 1 ? "" : "s"})` : ""}`}
               hint="Auto-detected from your variants' attributes — override if needed" testId="1688-field-variation-type">
          <select value={variationType} onChange={(e) => setVariationType(e.target.value)}
                  disabled={!editable} data-testid="1688-select-variation-type"
                  className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none bg-white">
            <option value="">— auto-detect —</option>
            <option value="color+size">Color + Size</option>
            <option value="color_only">Color only</option>
            <option value="size_only">Size only</option>
            <option value="set_of">Set of / Pack size</option>
            <option value="model">Model</option>
            <option value="capacity">Capacity</option>
            <option value="custom">Custom</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Unit cost (¥)" hint="Supplier's unit price in RMB — base for the Indian pricing formula" testId="1688-field-price-min">
          <input type="number" step="0.01" value={priceMin} onChange={(e) => setPriceMin(e.target.value)}
                 disabled={!editable} className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="Shipping (¥)" hint="Supplier's shipping cost per unit in RMB. Added to unit cost before FX conversion." testId="1688-field-shipping-cny">
          <input type="number" step="0.01" value={shippingCny} onChange={(e) => setShippingCny(e.target.value)}
                 disabled={!editable} data-testid="1688-input-shipping-cny"
                 className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="MOQ" testId="1688-field-moq">
          <input type="number" value={moq} onChange={(e) => setMoq(e.target.value)}
                 disabled={!editable} className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="Seller" testId="1688-field-seller">
          <input value={seller} onChange={(e) => setSeller(e.target.value)}
                 disabled={!editable} className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
      </div>
      <Field label="Chinese description">
        <textarea rows={3} value={descCn} onChange={(e) => setDescCn(e.target.value)}
                  disabled={!editable} className="w-full text-[12.5px] border border-slate-200 rounded-sm p-2 focus:ring-1 focus:ring-rose-500 outline-none" />
      </Field>
      <Field label="English description">
        <textarea rows={3} value={descEn} onChange={(e) => setDescEn(e.target.value)}
                  disabled={!editable} className="w-full text-[12.5px] border border-slate-200 rounded-sm p-2 focus:ring-1 focus:ring-rose-500 outline-none" />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Category path" hint="Slash / arrow separated, e.g. Kitchen > Drinkware > Insulated Bottles" testId="1688-field-category-path">
          <input value={categoryPath} onChange={(e) => setCategoryPath(e.target.value)}
                 disabled={!editable} data-testid="1688-input-category"
                 placeholder="Kitchen > Drinkware > Insulated Bottles"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="Amazon browse node ID" hint="Numeric browse node ID for Amazon India catalog" testId="1688-field-browse-node">
          <input value={browseNode} onChange={(e) => setBrowseNode(e.target.value)}
                 disabled={!editable} data-testid="1688-input-browse-node"
                 placeholder="e.g. 1379462031"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Brand name" testId="1688-field-brand-name">
          <input value={brandName} onChange={(e) => setBrandName(e.target.value)}
                 disabled={!editable} data-testid="1688-input-brand-name"
                 placeholder="e.g. Importrend"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="Product type" testId="1688-field-product-type">
          <input value={productType} onChange={(e) => setProductType(e.target.value)}
                 disabled={!editable} data-testid="1688-input-product-type"
                 placeholder="e.g. Sunglasses, Insulated Bottle, LED Strip"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="Product ID (1688)" hint="1688 offer identifier — auto-populated from source URL" testId="1688-field-product-id">
          <input value={detail.offer_id || ""} readOnly data-testid="1688-input-product-id"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 font-mono bg-slate-50 text-slate-700" />
        </Field>
        <Field label="Reseller margin %" hint="Applied on Landing Price to compute reseller SP" testId="1688-field-reseller-margin">
          <input type="number" step="0.1" value={resellerMarginPct} onChange={(e) => setResellerMarginPct(e.target.value)}
                 disabled={!editable} data-testid="1688-input-reseller-margin"
                 placeholder="e.g. 15"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
      </div>
      <Field label="Amazon commission category" hint="Amazon India referral-fee category — used to compute commission tier by item price" testId="1688-field-amazon-category">
        <select value={amazonCategory} onChange={(e) => setAmazonCategory(e.target.value)}
                disabled={!editable} data-testid="1688-select-amazon-category"
                className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none bg-white">
          <option value="">— pick a category —</option>
          {amazonCategories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Comments / notes" testId="1688-field-comments">
        <textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)}
                  disabled={!editable} data-testid="1688-input-comments"
                  placeholder="Internal notes — reorder cadence, quality concerns, seasonal demand…"
                  className="w-full text-[12.5px] border border-slate-200 rounded-sm p-2 focus:ring-1 focus:ring-rose-500 outline-none" />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Video URL" hint="Product demo video (YouTube, Vimeo, MP4) — shown on marketplace listings that support video" testId="1688-field-video-url">
          <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                 disabled={!editable} data-testid="1688-input-video-url"
                 placeholder="https://www.youtube.com/watch?v=…"
                 className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none" />
        </Field>
        <Field label="Workflow status" hint="Where this SKU sits in the sourcing → listing pipeline" testId="1688-field-workflow-status">
          <select value={workflowStatus} onChange={(e) => setWorkflowStatus(e.target.value)}
                  disabled={!editable} data-testid="1688-select-workflow-status"
                  className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none bg-white">
            <option value="pending_pricing">Pending for Pricing</option>
            <option value="ready_for_listing">Ready for Listing</option>
          </select>
        </Field>
      </div>
      <div className="border border-slate-200 rounded-sm bg-slate-50 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Listing lifecycle</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Track ownership + toggle &ldquo;Listed&rdquo; once live on marketplaces.</div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="1688-toggle-is-listed">
            <input type="checkbox" checked={isListed} onChange={(e) => setIsListed(e.target.checked)}
                   disabled={!editable} className="accent-emerald-600 w-4 h-4" />
            <span className={`text-[12px] font-bold uppercase tracking-wider ${isListed ? "text-emerald-700" : "text-slate-500"}`}>
              {isListed ? "Yes — Listed" : "No — Not listed"}
            </span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Scraped by" testId="1688-field-scraped-by">
            <select value={scrapedBy} onChange={(e) => setScrapedBy(e.target.value)}
                    disabled={!editable} data-testid="1688-select-scraped-by"
                    className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none bg-white">
              <option value="">— unassigned —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </Field>
          <Field label="Listed by" testId="1688-field-listed-by">
            <select value={listedBy} onChange={(e) => setListedBy(e.target.value)}
                    disabled={!editable} data-testid="1688-select-listed-by"
                    className="w-full h-8 text-[13px] border border-slate-200 rounded-sm px-2 focus:ring-1 focus:ring-rose-500 outline-none bg-white">
              <option value="">— unassigned —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </Field>
          <Field label="Scraped date" hint="When the product was pulled from 1688" testId="1688-field-scraped-at">
            <input type="datetime-local" value={scrapedAt} onChange={(e) => setScrapedAt(e.target.value)}
                   disabled={!editable} data-testid="1688-input-scraped-at"
                   className="w-full h-8 text-[12.5px] border border-slate-200 rounded-sm px-2 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[11.5px] text-slate-600">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Currently scraped by</div>
            <div className="mt-0.5" data-testid="1688-provenance-scraped">
              {detail.scraped_by_name || detail.scraped_by_email || <em className="text-slate-400">—</em>}
              {detail.scraped_at && <span className="ml-2 text-slate-400 font-mono">on {new Date(detail.scraped_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Currently listed by</div>
            <div className="mt-0.5" data-testid="1688-provenance-listed">
              {detail.listed_by_name || detail.listed_by_email || <em className="text-slate-400">— not yet</em>}
              {detail.listed_at && <span className="ml-2 text-slate-400 font-mono">on {new Date(detail.listed_at).toLocaleDateString()}</span>}
            </div>
          </div>
        </div>
      </div>
      <Field label="Source image URLs (one per line)" hint="Paste alicdn / 1688 image URLs — used as inputs for background removal & master image">
        <textarea rows={4} value={imgsRaw} onChange={(e) => setImgsRaw(e.target.value)}
                  disabled={!editable} data-testid="1688-field-images"
                  className="w-full text-[11.5px] font-mono border border-slate-200 rounded-sm p-2 focus:ring-1 focus:ring-rose-500 outline-none" />
      </Field>
      {editable && (
        <div className="flex justify-end">
          <Button variant="primary" onClick={save} disabled={saving} testId="1688-save-btn">
            {saving ? <><RefreshCw size={12} className="animate-spin" /> Saving…</> : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ImagesTab({ detail, imgUrl }) {
  const gi = detail.generated_images || {};
  const slots = ["master", "lifestyle", "in_use", "hero", "features", "detail", "compare"];
  const gen = slots.filter((s) => gi[s]?.storage_path);
  return (
    <div className="p-4 space-y-4">
      {gen.length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Generated images ({gen.length})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {gen.map((s) => (
              <div key={s} data-testid={`1688-img-${s}`}
                   className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
                <div className="aspect-square bg-white flex items-center justify-center">
                  <img referrerPolicy="no-referrer" src={imgUrl(s)} alt={gi[s].label} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="border-t border-slate-200 px-2 py-1 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-800">{gi[s].label}</span>
                  <a href={imgUrl(s)} download={`${detail.offer_id}-${s}.jpg`} target="_blank" rel="noreferrer"
                     className="text-[10.5px] text-blue-600 hover:underline inline-flex items-center gap-0.5">
                    <Download size={9} /> {gi[s].method}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(detail.images || []).length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Source images ({detail.images.length})
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {detail.images.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`1688-src-img-${i}`}
                 className="w-28 h-28 shrink-0 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden flex items-center justify-center hover:border-rose-300">
                <img referrerPolicy="no-referrer" src={u} alt="" className="max-w-full max-h-full object-contain" />
              </a>
            ))}
          </div>
        </div>
      )}
      {gen.length === 0 && (detail.images || []).length === 0 && (
        <div className="p-8 text-center text-slate-500 text-[13px]">
          No images. Add source image URLs in the Overview tab, then click <strong>Generate images</strong>.
        </div>
      )}
    </div>
  );
}

function PricingTab({ draft, setDraft, detail, onApply, regenLoading, editable }) {
  const p = detail.pricing || {};
  const b = p.breakdown || [];
  const variants = detail.variants || [];
  const variantPricing = detail.variant_pricing || {};
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const num = (v) => (v === "" || v == null ? "" : Number(v));
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Pricing knobs</div>
          <div className="grid grid-cols-2 gap-3">
            <KnobInput label="FX rate CNY → INR" k="cny_to_inr_rate" v={num(draft.cny_to_inr_rate)} onChange={(v) => set("cny_to_inr_rate", v)} suffix="₹/¥" disabled={!editable} />
            <KnobInput label="Local shipping / unit (₹)" k="shipping_per_unit_inr" v={num(draft.shipping_per_unit_inr)} onChange={(v) => set("shipping_per_unit_inr", v)} suffix="₹" disabled={!editable} />
            <KnobInput label="GST %" k="gst_percent" v={num(draft.gst_percent)} onChange={(v) => set("gst_percent", v)} suffix="%" disabled={!editable} />
            <KnobInput label="Margin %" k="margin_percent" v={num(draft.margin_percent)} onChange={(v) => set("margin_percent", v)} suffix="%" disabled={!editable} />
            <KnobInput label="Marketplace fee %" k="marketplace_fee_percent" v={num(draft.marketplace_fee_percent)} onChange={(v) => set("marketplace_fee_percent", v)} suffix="%" disabled={!editable} />
            <KnobInput label="Buffer %" k="buffer_percent" v={num(draft.buffer_percent)} onChange={(v) => set("buffer_percent", v)} suffix="%" disabled={!editable} />
          </div>
          {editable && (
            <div className="mt-3">
              <Button variant="primary" onClick={() => onApply({
                cny_to_inr_rate: parseFloat(draft.cny_to_inr_rate),
                shipping_per_unit_inr: parseFloat(draft.shipping_per_unit_inr),
                gst_percent: parseFloat(draft.gst_percent),
                margin_percent: parseFloat(draft.margin_percent),
                marketplace_fee_percent: parseFloat(draft.marketplace_fee_percent),
                buffer_percent: parseFloat(draft.buffer_percent),
              })} disabled={regenLoading} testId="1688-apply-pricing">
                {regenLoading ? <><RefreshCw size={12} className="animate-spin" /> Calculating…</> : <><Calculator size={12} /> Recalculate</>}
              </Button>
            </div>
          )}
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Parent breakdown (using parent&apos;s unit cost + shipping ¥)</div>
          <div className="border border-slate-200 rounded-sm bg-slate-50 divide-y divide-slate-200">
            {b.map((row, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 text-[12px]" data-testid={`1688-breakdown-${i}`}>
                <span className="text-slate-700">{row.label}</span>
                <span className="font-mono font-semibold text-slate-900">{row.unit}{row.value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 text-[13px] bg-emerald-50">
              <span className="font-semibold text-emerald-900">Final selling price</span>
              <span className="font-mono font-bold text-emerald-900" data-testid="1688-final-inr">₹{p.final_inr ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 text-[13px] bg-slate-100">
              <span className="font-semibold text-slate-900">Suggested MRP (+25%)</span>
              <span className="font-mono font-bold text-slate-900" data-testid="1688-mrp-inr">₹{p.mrp_inr ?? "—"}</span>
            </div>
          </div>
        </div>
      </div>

      {variants.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
              Detailed pricing chain (server-computed) — {variants.length} variant{variants.length === 1 ? "" : "s"}
            </div>
            {editable && (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/1688/${detail.id}/recalc-pricing`);
                    toast.success("Pricing recomputed from current settings + variant fields");
                    // Bubble up via parent onApply to refresh detail
                    if (data?.variant_pricing) {
                      // A hack: reload the parent
                      window.location.reload();
                    }
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                data-testid="1688-recalc-pricing-server"
                className="h-7 px-2 text-[11px] font-semibold uppercase tracking-wider bg-blue-50 text-blue-800 border border-blue-200 rounded-sm hover:bg-blue-100 inline-flex items-center gap-1"
              >
                <Calculator size={11} /> Recalc from server
              </button>
            )}
          </div>
          <div className="border border-slate-200 rounded-sm overflow-x-auto mb-4">
            <table className="erp-table w-full text-[11px] font-mono" data-testid="1688-variant-fullpricing-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left sticky left-0 bg-slate-100">Variant</th>
                  <th className="text-right" title="Last-mile INR">LM</th>
                  <th className="text-right" title="Variant Costing INR">Cost</th>
                  <th className="text-right" title="International Freight INR">Freight</th>
                  <th className="text-right" title="Aggregator Margin INR">Agg</th>
                  <th className="text-right" title="GST on landing chain INR">GST</th>
                  <th className="text-right bg-slate-200" title="Landing Price INR">Land ₹</th>
                  <th className="text-right" title="Importrend Margin INR">Margin</th>
                  <th className="text-right" title="Margin GST INR">M-GST</th>
                  <th className="text-right bg-slate-200" title="Transfer Price INR">Xfer ₹</th>
                  <th className="text-right" title="Reseller Margin INR">Reseller</th>
                  <th className="text-right" title="Amazon commission % from category tier">Amz%</th>
                  <th className="text-right" title="Amazon commission INR">Amz</th>
                  <th className="text-right bg-emerald-100" title="Calculated Selling Price INR">SP ₹</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => {
                  const vp = variantPricing[v.id] || {};
                  return (
                    <tr key={v.id} className="border-t border-slate-100" data-testid={`1688-fullpricing-row-${v.id}`}>
                      <td className="text-[11.5px] font-sans font-medium text-slate-800 sticky left-0 bg-white">{v.name}</td>
                      <td className="text-right">₹{vp.last_mile ?? 0}</td>
                      <td className="text-right">₹{vp.variant_costing ?? 0}</td>
                      <td className="text-right">₹{vp.intl_freight ?? 0}</td>
                      <td className="text-right">₹{vp.aggregator_margin ?? 0}</td>
                      <td className="text-right">₹{vp.variant_gst ?? 0}</td>
                      <td className="text-right bg-slate-50 font-semibold text-slate-900">₹{vp.landing_price ?? 0}</td>
                      <td className="text-right">₹{vp.variant_margin ?? 0}</td>
                      <td className="text-right">₹{vp.margin_gst ?? 0}</td>
                      <td className="text-right bg-slate-50 font-semibold text-slate-900">₹{vp.transfer_price ?? 0}</td>
                      <td className="text-right">₹{vp.reseller_margin ?? 0}</td>
                      <td className="text-right text-slate-500">{vp.amazon_commission_pct ?? "—"}%</td>
                      <td className="text-right">₹{vp.amazon_commission ?? 0}</td>
                      <td className="text-right bg-emerald-50 font-bold text-emerald-900" data-testid={`1688-calculated-sp-${v.id}`}>₹{vp.calculated_sp ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
              Legacy per-variant pricing (using the knobs above)
            </div>
          </div>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="erp-table w-full" data-testid="1688-variant-pricing-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left">Variant</th>
                  <th className="text-left">SKU</th>
                  <th className="text-right">Unit ¥</th>
                  <th className="text-right">Ship ¥</th>
                  <th className="text-right">Landed ₹</th>
                  <th className="text-right">Final ₹</th>
                  <th className="text-right">MRP ₹</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => {
                  const vp = variantPricing[v.id] || {};
                  return (
                    <tr key={v.id} className="border-t border-slate-100" data-testid={`1688-variant-pricing-row-${v.id}`}>
                      <td className="text-[12px] font-medium text-slate-800">
                        {v.name}
                        <div className="mt-0.5 flex flex-wrap gap-0.5">
                          {Object.entries(v.attrs || {}).slice(0, 3).map(([k, val]) => (
                            <span key={k} className="inline-flex items-center h-4 px-1 rounded-sm bg-rose-50 border border-rose-200 text-rose-800 text-[10px]">{k}:{val}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-[11.5px] font-mono">{v.sku || "—"}</td>
                      <td className="text-[11.5px] font-mono text-right text-amber-800">¥{v.price_cny ?? "—"}</td>
                      <td className="text-[11.5px] font-mono text-right text-amber-800">¥{v.shipping_cny ?? "—"}</td>
                      <td className="text-[11.5px] font-mono text-right text-slate-600">₹{vp.landed_inr ?? "—"}</td>
                      <td className="text-[12px] font-mono text-right font-semibold text-emerald-800">₹{vp.final_inr ?? "—"}</td>
                      <td className="text-[11.5px] font-mono text-right text-slate-600">₹{vp.mrp_inr ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecsTab({ detail, onSave, editable }) {
  const [pairs, setPairs] = useState(() => Object.entries(detail.specifications || {}).map(([k, v]) => ({ k, v })));
  const [pairsEn, setPairsEn] = useState(() => Object.entries(detail.specifications_en || {}).map(([k, v]) => ({ k, v })));
  const upd = (arr, setter, i, field, val) => { const n = arr.slice(); n[i] = { ...n[i], [field]: val }; setter(n); };
  const add = (arr, setter) => setter([...arr, { k: "", v: "" }]);
  const del = (arr, setter, i) => setter(arr.filter((_, idx) => idx !== i));
  const save = () => {
    const specifications = Object.fromEntries(pairs.filter((p) => p.k).map((p) => [p.k, p.v]));
    const specifications_en = Object.fromEntries(pairsEn.filter((p) => p.k).map((p) => [p.k, p.v]));
    onSave({ specifications, specifications_en });
  };
  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      <SpecList title="Chinese specs (原始)" pairs={pairs} setPairs={setPairs} onChange={upd} onAdd={add} onDel={del} editable={editable} testPrefix="1688-spec-cn" />
      <SpecList title="English specs (translated)" pairs={pairsEn} setPairs={setPairsEn} onChange={upd} onAdd={add} onDel={del} editable={editable} testPrefix="1688-spec-en" />
      {editable && (
        <div className="md:col-span-2 flex justify-end">
          <Button variant="primary" onClick={save} testId="1688-save-specs">Save specs</Button>
        </div>
      )}
    </div>
  );
}

function SpecList({ title, pairs, setPairs, onChange, onAdd, onDel, editable, testPrefix }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center justify-between">
        {title}
        {editable && <button onClick={() => onAdd(pairs, setPairs)} data-testid={`${testPrefix}-add`}
                             className="text-[10.5px] text-rose-600 hover:underline inline-flex items-center gap-0.5"><Plus size={10} /> add</button>}
      </div>
      <div className="border border-slate-200 rounded-sm">
        {pairs.length === 0 && <div className="p-4 text-center text-[12px] text-slate-500">No specs</div>}
        {pairs.map((p, i) => (
          <div key={i} className="flex items-center gap-1 border-b border-slate-100 last:border-0 px-1.5 py-1" data-testid={`${testPrefix}-row-${i}`}>
            <input value={p.k} onChange={(e) => onChange(pairs, setPairs, i, "k", e.target.value)}
                   disabled={!editable}
                   className="w-40 h-7 px-2 text-[12px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            <input value={p.v} onChange={(e) => onChange(pairs, setPairs, i, "v", e.target.value)}
                   disabled={!editable}
                   className="flex-1 h-7 px-2 text-[12px] border border-slate-200 rounded-sm focus:ring-1 focus:ring-rose-500 outline-none" />
            {editable && (
              <button onClick={() => onDel(pairs, setPairs, i)} data-testid={`${testPrefix}-del-${i}`}
                      className="text-slate-400 hover:text-red-600"><X size={12} /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------- Amazon India Listing tab ----------------------------------------

function AmazonListingTab({ rid, detail, onChange, onRefresh, editable }) {
  const [busy, setBusy] = useState(false);
  const listing = detail.amazon_listing;
  const [titleDraft, setTitleDraft] = useState(listing?.title || "");
  const [titleSaving, setTitleSaving] = useState(false);
  useEffect(() => { setTitleDraft(listing?.title || ""); }, [listing?.title]);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/1688/${rid}/generate-amazon-listing`);
      onChange(data);
      toast.success("Amazon listing generated");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const saveTitle = async () => {
    setTitleSaving(true);
    try {
      const { data } = await api.patch(`/1688/${rid}`, {
        amazon_listing: { ...(listing || {}), title: titleDraft },
      });
      onChange(data);
      toast.success("Amazon title saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setTitleSaving(false);
  };
  const copy = (text, label) => {
    navigator.clipboard.writeText(text || "");
    toast.success(`${label} copied`);
  };

  if (!listing) {
    return (
      <div className="p-6 text-center">
        <ShieldCheck size={28} className="mx-auto text-rose-500 mb-2" />
        <div className="text-[13px] font-semibold text-slate-800 mb-1">Amazon India Listing Generator</div>
        <div className="text-[12px] text-slate-500 max-w-md mx-auto mb-4">
          Uses Claude Sonnet (via your Universal Key) to build a policy-compliant Amazon India listing:
          75-char title, 5 bullets (10-200 chars), 200-300 word description,
          &lt;250-byte backend search terms, specs, keywords, and a 7-image plan.
        </div>
        {editable ? (
          <Button variant="primary" onClick={generate} disabled={busy} testId="1688-generate-amazon">
            {busy ? <><RefreshCw size={12} className="animate-spin" /> Generating…</> : <><Sparkles size={12} /> Generate Amazon Listing</>}
          </Button>
        ) : (
          <div className="text-[11.5px] text-slate-500 italic">You don&apos;t have edit permission for this module</div>
        )}
      </div>
    );
  }

  const warnings = listing.warnings || [];
  const titleLen = (listing.title || "").length;
  const searchBytes = new TextEncoder().encode(listing.search_terms || "").length;
  const descWords = (listing.description || "").split(/\s+/).filter(Boolean).length;

  return (
    <div className="p-4 space-y-4">
      {/* Header + regenerate */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {warnings.length === 0
            ? <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10.5px] font-bold uppercase tracking-wider"><ShieldCheck size={11} /> Policy compliant</span>
            : <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-amber-50 text-amber-900 border border-amber-300 text-[10.5px] font-bold uppercase tracking-wider" data-testid="1688-amazon-warnings"><AlertTriangle size={11} /> {warnings.length} warning{warnings.length === 1 ? "" : "s"}</span>}
          {detail.amazon_listing_generated_at && (
            <span className="text-[10.5px] text-slate-500 font-mono">generated {ago(detail.amazon_listing_generated_at)}</span>
          )}
        </div>
        {editable && (
          <Button onClick={generate} disabled={busy} testId="1688-regenerate-amazon">
            {busy ? <><RefreshCw size={12} className="animate-spin" /> Generating…</> : <><Wand2 size={12} /> Re-generate</>}
          </Button>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm text-[11.5px] text-amber-900">
          <div className="font-semibold mb-1">Policy warnings:</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {warnings.map((w, i) => <li key={i} data-testid={`1688-warning-${i}`}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Title (editable) */}
      <ListingBlock title={`Title  •  ${titleDraft.length}/75 chars${titleDraft.length > 75 ? "  ⚠ over limit" : ""}`}
                    onCopy={() => copy(titleDraft, "Title")} testId="1688-amazon-title">
        <div className="space-y-2">
          <textarea
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            disabled={!editable}
            rows={2}
            data-testid="1688-amazon-title-input"
            className={`w-full p-2 text-[13px] font-medium border rounded-sm focus:outline-none focus:ring-1 focus:ring-rose-500 ${titleDraft.length > 75 ? "text-red-700 bg-red-50 border-red-300" : "text-slate-900 bg-slate-50 border-slate-200"}`}
          />
          {editable && titleDraft !== (listing.title || "") && (
            <div className="flex justify-end">
              <Button variant="primary" onClick={saveTitle} disabled={titleSaving} testId="1688-amazon-title-save">
                {titleSaving ? <><RefreshCw size={12} className="animate-spin" /> Saving…</> : "Save title"}
              </Button>
            </div>
          )}
        </div>
      </ListingBlock>

      {/* Bullets */}
      <ListingBlock title={`Bullet points  •  ${(listing.bullets || []).length}/5`}
                    onCopy={() => copy((listing.bullets || []).join("\n"), "Bullets")}
                    testId="1688-amazon-bullets">
        <ul className="space-y-1 border border-slate-200 rounded-sm bg-slate-50 p-2">
          {(listing.bullets || []).map((b, i) => {
            const n = b.length;
            const bad = n < 10 || n > 200 || b.rstrip?.().endsWith?.(".");
            return (
              <li key={i} data-testid={`1688-amazon-bullet-${i}`} className="flex gap-2 text-[12.5px]">
                <span className={`shrink-0 font-mono text-[10.5px] w-16 text-right pt-0.5 ${bad ? "text-red-600" : "text-slate-400"}`}>[{n}]</span>
                <span className="text-slate-800">{b}</span>
              </li>
            );
          })}
        </ul>
      </ListingBlock>

      {/* Description */}
      <ListingBlock title={`Description  •  ${descWords} words (target 200-300)`}
                    onCopy={() => copy(listing.description, "Description")}
                    testId="1688-amazon-description">
        <div className="p-2 text-[12.5px] text-slate-800 whitespace-pre-wrap border border-slate-200 rounded-sm bg-slate-50 max-h-56 overflow-auto">
          {listing.description || <span className="text-slate-400">—</span>}
        </div>
      </ListingBlock>

      {/* Backend Search Terms */}
      <ListingBlock title={`Backend Search Terms  •  ${searchBytes}/250 bytes`}
                    onCopy={() => copy(listing.search_terms, "Search terms")}
                    testId="1688-amazon-search-terms">
        <div className={`p-2 text-[12px] font-mono border border-slate-200 rounded-sm ${searchBytes > 250 ? "text-red-700 bg-red-50" : "text-slate-800 bg-slate-50"}`}>
          {listing.search_terms || <span className="text-slate-400">—</span>}
        </div>
      </ListingBlock>

      {/* Specs grid */}
      <ListingBlock title="Product specifications"
                    onCopy={() => copy(Object.entries(listing.specifications || {}).map(([k, v]) => `${k}: ${v}`).join("\n"), "Specs")}
                    testId="1688-amazon-specs">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 border border-slate-200 rounded-sm bg-slate-50 p-3">
          {Object.entries(listing.specifications || {}).map(([k, v]) => (
            <div key={k} className="flex text-[12px]" data-testid={`1688-amazon-spec-${k}`}>
              <span className="w-40 font-mono text-slate-500">{k}</span>
              <span className="flex-1 text-slate-800 font-medium">{v}</span>
            </div>
          ))}
        </div>
      </ListingBlock>

      {/* Keywords */}
      <ListingBlock title="Search keywords"
                    onCopy={() => copy(JSON.stringify(listing.keywords, null, 2), "Keywords")}
                    testId="1688-amazon-keywords">
        {["primary", "secondary", "long_tail"].map((k) => (
          <div key={k} className="mb-1">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{k.replace("_", "-")}</div>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {(listing.keywords?.[k] || []).map((w, i) => (
                <span key={i} className="inline-flex items-center h-5 px-1.5 rounded-sm bg-rose-50 border border-rose-200 text-rose-800 text-[11px]">{w}</span>
              ))}
              {(listing.keywords?.[k] || []).length === 0 && <span className="text-[11px] text-slate-400 italic">—</span>}
            </div>
          </div>
        ))}
      </ListingBlock>

      {/* Image plan */}
      <ListingBlock title="Image plan (7 slots — Amazon rules)"
                    onCopy={() => copy((listing.image_suggestions || []).join("\n"), "Image plan")}
                    testId="1688-amazon-image-plan">
        <ol className="list-decimal pl-5 space-y-1 border border-slate-200 rounded-sm bg-slate-50 p-3 text-[12px] text-slate-800">
          {(listing.image_suggestions || []).map((s, i) => (
            <li key={i} data-testid={`1688-amazon-image-plan-${i}`}>{s}</li>
          ))}
        </ol>
      </ListingBlock>
    </div>
  );
}

function ListingBlock({ title, children, onCopy, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
        <button onClick={onCopy} className="inline-flex items-center gap-1 text-[10.5px] text-blue-600 hover:underline">
          <Copy size={10} /> copy
        </button>
      </div>
      {children}
    </div>
  );
}

// ------- Variants tab ------------------------------------------------------

function VariantsTab({ rid, detail, onChange, onRefresh, editable }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // variant object being edited
  const [showAdd, setShowAdd] = useState(false);
  const variants = detail.variants || [];

  const reload = async () => {
    try { const { data } = await api.get(`/1688/${rid}`); onChange(data); }
    catch (e) { /* silent */ }
  };

  const addVariant = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/1688/${rid}/variants`, payload);
      toast.success("Variant added");
      setShowAdd(false);
      await reload(); onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const patchVariant = async (vid, payload) => {
    setBusy(true);
    try {
      await api.patch(`/1688/${rid}/variants/${vid}`, payload);
      toast.success("Saved");
      setEditing(null);
      await reload(); onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const removeVariant = async (vid) => {
    if (!window.confirm("Delete this variant?")) return;
    try {
      await api.delete(`/1688/${rid}/variants/${vid}`);
      toast.success("Deleted");
      await reload(); onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadImagesFor = async (vid, files) => {
    if (!files || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      await api.post(`/1688/${rid}/variants/${vid}/upload-images`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Images uploaded");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeManualImage = async (vid, idx) => {
    try {
      await api.delete(`/1688/${rid}/variants/${vid}/manual-images/${idx}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const genImagesFor = async (vid) => {
    try {
      await api.post(`/1688/${rid}/variants/${vid}/generate-images`);
      toast.info("Image gen started — ~30-60s. Polling…");
      // simple 15s poll
      let attempts = 0;
      const t = setInterval(async () => {
        attempts++;
        try {
          const { data } = await api.get(`/1688/${rid}`);
          onChange(data);
          const v = (data.variants || []).find((x) => x.id === vid);
          if (!v || v.image_job_status !== "running" || attempts > 12) clearInterval(t);
        } catch (_e) { clearInterval(t); }
      }, 6000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
          Variants ({variants.length}) — variation type: <span className="font-mono text-rose-700">{detail.variation_type || "auto"}</span>
        </div>
        {editable && (
          <Button variant="primary" onClick={() => setShowAdd(true)} testId="1688-variant-add">
            <Plus size={12} /> Add variant
          </Button>
        )}
      </div>

      {variants.length === 0 && (
        <div className="p-8 text-center border border-dashed border-slate-300 rounded-sm bg-slate-50 text-[13px] text-slate-500">
          No variants yet.
          {editable && <> Click <strong>Add variant</strong> to create SKU-level variations (color / size / model / etc.)</>}
        </div>
      )}

      {variants.map((v) => (
        <VariantCard key={v.id} rid={rid} v={v} editable={editable}
                     variantPricing={(detail.variant_pricing || {})[v.id]}
                     onEdit={() => setEditing(v)} onDelete={() => removeVariant(v.id)}
                     onUploadImages={(files) => uploadImagesFor(v.id, files)}
                     onRemoveManualImage={(i) => removeManualImage(v.id, i)}
                     onGenImages={() => genImagesFor(v.id)} />
      ))}

      {showAdd && (
        <VariantModal onClose={() => setShowAdd(false)} onSave={addVariant} busy={busy} />
      )}
      {editing && (
        <VariantModal variant={editing} onClose={() => setEditing(null)}
                      onSave={(payload) => patchVariant(editing.id, payload)} busy={busy} />
      )}
    </div>
  );
}

function VariantCard({ rid, v, variantPricing, editable, onEdit, onDelete, onUploadImages, onRemoveManualImage, onGenImages }) {
  const inputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const attrEntries = Object.entries(v.attrs || {});
  const gen = v.generated_images || {};
  const genCount = Object.keys(gen).length;
  const manualImgs = v.manual_images || [];
  const running = v.image_job_status === "running";
  const priceInfo = variantPricing || {};
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-3" data-testid={`1688-variant-card-${v.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[13px] text-slate-900">{v.name || "(unnamed variant)"}</span>
            {attrEntries.map(([k, val]) => (
              <span key={k} className="inline-flex items-center h-5 px-1.5 rounded-sm bg-rose-50 border border-rose-200 text-rose-800 text-[11px]" data-testid={`1688-variant-attr-${v.id}-${k}`}>
                {k}: <strong className="ml-0.5">{val}</strong>
              </span>
            ))}
            <span className="font-mono text-[10.5px] text-slate-400">{v.id}</span>
          </div>
          <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-0.5 text-[11.5px] text-slate-600">
            <span data-testid={`1688-variant-sku-${v.id}`}>SKU: <span className="font-mono">{v.sku || "—"}</span></span>
            <span>Unit cost: <span className="font-mono text-amber-800">¥{v.price_cny ?? "—"}</span></span>
            <span>Shipping: <span className="font-mono text-amber-800">¥{v.shipping_cny ?? "—"}</span></span>
            <span>Stock: <span className="font-mono">{v.stock ?? 0}</span></span>
            <span className="col-span-2 md:col-span-4">
              <span className="text-emerald-700 font-semibold">₹{priceInfo.final_inr ?? "—"}</span>
              <span className="text-slate-400"> selling price (auto)</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="text-slate-700">MRP <span className="font-mono">₹{priceInfo.mrp_inr ?? "—"}</span></span>
            </span>
          </div>
        </div>
        {editable && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onEdit} data-testid={`1688-variant-edit-${v.id}`}
                    className="text-[11.5px] text-blue-600 hover:underline">Edit</button>
            <button onClick={onDelete} data-testid={`1688-variant-delete-${v.id}`}
                    className="text-[11.5px] text-red-600 hover:underline">Delete</button>
          </div>
        )}
      </div>

      {/* Manual images */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
            Manual images ({manualImgs.length})
          </div>
          {editable && (
            <>
              <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
                     data-testid={`1688-variant-file-input-${v.id}`}
                     onChange={(e) => { onUploadImages(e.target.files); e.target.value = ""; }} />
              <button onClick={() => inputRef.current?.click()} data-testid={`1688-variant-upload-${v.id}`}
                      className="text-[10.5px] text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                <Upload2 size={10} /> upload files
              </button>
            </>
          )}
        </div>
        {manualImgs.length > 0 ? (
          <div className="flex gap-2 flex-wrap">
            {manualImgs.map((m) => {
              const url = `${backend}/api/1688/${rid}/variants/${v.id}/manual-images/${m.index}`;
              return (
                <div key={m.index} className="relative w-20 h-20 border border-slate-200 rounded-sm overflow-hidden group" data-testid={`1688-variant-manual-img-${v.id}-${m.index}`}>
                  <img referrerPolicy="no-referrer" src={url} alt="" className="w-full h-full object-contain bg-slate-50" />
                  {editable && (
                    <button onClick={() => onRemoveManualImage(m.index)}
                            className="absolute top-0.5 right-0.5 w-4 h-4 bg-slate-900/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-slate-900">
                      <X size={9} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 italic">No manual images uploaded.</div>
        )}
      </div>

      {/* Generated images */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
            AI-generated images {genCount > 0 && `(${genCount})`}
          </div>
          {editable && (
            <button onClick={onGenImages} disabled={running} data-testid={`1688-variant-gen-${v.id}`}
                    className="text-[10.5px] text-rose-600 hover:underline inline-flex items-center gap-0.5 disabled:opacity-50">
              {running ? <><Loader2 size={10} className="animate-spin" /> Generating…</> : <><Wand2 size={10} /> Generate</>}
            </button>
          )}
        </div>
        {genCount > 0 ? (
          <div className="flex gap-2 flex-wrap">
            {["master", "lifestyle", "features", "dimensions", "material", "angles", "package"].filter((s) => gen[s]?.storage_path).map((slot) => {
              const url = `${backend}/api/1688/${rid}/variants/${v.id}/images/${slot}`;
              return (
                <a key={slot} href={url} target="_blank" rel="noreferrer"
                   className="relative w-20 h-20 border border-slate-200 rounded-sm overflow-hidden hover:border-rose-300"
                   data-testid={`1688-variant-gen-img-${v.id}-${slot}`}>
                  <img referrerPolicy="no-referrer" src={url} alt={gen[slot].label} className="w-full h-full object-contain bg-slate-50" />
                  <span className="absolute bottom-0 left-0 right-0 bg-slate-900/60 text-white text-[9px] px-1 truncate">{gen[slot].label}</span>
                </a>
              );
            })}
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 italic">Not generated yet.</div>
        )}
      </div>
    </div>
  );
}

function VariantModal({ variant, onClose, onSave, busy }) {
  const [name, setName] = useState(variant?.name || "");
  const [sku, setSku] = useState(variant?.sku || "");
  const [priceCny, setPriceCny] = useState(variant?.price_cny ?? "");
  const [shippingCny, setShippingCny] = useState(variant?.shipping_cny ?? "");
  const [shippingInr, setShippingInr] = useState(variant?.shipping_inr ?? "");
  const [weightGrams, setWeightGrams] = useState(variant?.weight_grams ?? "");
  const [variantMoq, setVariantMoq] = useState(variant?.moq ?? "");
  const [productId, setProductId] = useState(variant?.product_id || "");
  const [stock, setStock] = useState(variant?.stock ?? 0);
  const [attrs, setAttrs] = useState(() => Object.entries(variant?.attrs || {}).map(([k, v]) => ({ k, v })));
  const setAttr = (i, field, val) => { const a = attrs.slice(); a[i] = { ...a[i], [field]: val }; setAttrs(a); };
  const addAttr = () => setAttrs([...attrs, { k: "", v: "" }]);
  const delAttr = (i) => setAttrs(attrs.filter((_, idx) => idx !== i));

  const submit = () => {
    const payload = {
      name, sku,
      price_cny: priceCny === "" ? null : parseFloat(priceCny),
      shipping_cny: shippingCny === "" ? null : parseFloat(shippingCny),
      shipping_inr: shippingInr === "" ? null : parseFloat(shippingInr),
      weight_grams: weightGrams === "" ? null : parseFloat(weightGrams),
      moq: variantMoq === "" ? null : parseInt(variantMoq, 10),
      product_id: productId || "",
      stock: parseInt(stock || 0, 10),
      attrs: Object.fromEntries(attrs.filter((a) => a.k).map((a) => [a.k, a.v])),
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-[520px] max-w-full bg-white border border-slate-200 rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="h-11 border-b border-slate-200 flex items-center justify-between px-4">
          <div className="font-heading text-[13px] font-semibold text-slate-900">
            {variant ? "Edit variant" : "New variant"}
          </div>
          <button onClick={onClose} data-testid="1688-variant-modal-close" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name / Label"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="1688-variant-name"
                                                className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm focus:ring-1 focus:ring-rose-500 outline-none" /></Field>
            <Field label="SKU"><input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="1688-variant-sku-input"
                                       className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Unit cost (¥)" hint="Supplier's per-unit price in RMB">
              <input type="number" step="0.01" value={priceCny} onChange={(e) => setPriceCny(e.target.value)} data-testid="1688-variant-price-cny"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            </Field>
            <Field label="Shipping (¥) legacy" hint="Optional legacy field — new pricing uses Shipping (₹)">
              <input type="number" step="0.01" value={shippingCny} onChange={(e) => setShippingCny(e.target.value)} data-testid="1688-variant-shipping-cny"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            </Field>
            <Field label="Stock"><input type="number" value={stock} onChange={(e) => setStock(e.target.value)} data-testid="1688-variant-stock"
                                         className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" /></Field>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <Field label="Shipping (₹)" hint="Supplier shipping in INR — used by pricing formula">
              <input type="number" step="0.01" value={shippingInr} onChange={(e) => setShippingInr(e.target.value)} data-testid="1688-variant-shipping-inr"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            </Field>
            <Field label="Weight (g)" hint="Variant weight in grams — drives last-mile + intl freight">
              <input type="number" step="1" value={weightGrams} onChange={(e) => setWeightGrams(e.target.value)} data-testid="1688-variant-weight"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            </Field>
            <Field label="MOQ" hint="Minimum order quantity for pricing calc">
              <input type="number" step="1" value={variantMoq} onChange={(e) => setVariantMoq(e.target.value)} data-testid="1688-variant-moq"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            </Field>
            <Field label="Product ID" hint="External SKU or Amazon ASIN">
              <input value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="1688-variant-product-id"
                     placeholder="e.g. B0XXXXXX"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
            </Field>
          </div>
          <div className="p-2 rounded-sm bg-blue-50 border border-blue-200 text-[11px] text-blue-900">
            <strong>Indian selling price is auto-calculated</strong> from Unit cost + Shipping using the Pricing tab formula (FX rate, GST, margin, marketplace fee, buffer).
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Attributes</div>
              <button onClick={addAttr} data-testid="1688-variant-attr-add" className="text-[10.5px] text-rose-600 hover:underline inline-flex items-center gap-0.5"><Plus size={10} /> add</button>
            </div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {attrs.map((p, i) => (
                <div key={i} className="flex items-center gap-1" data-testid={`1688-variant-attr-row-${i}`}>
                  <input value={p.k} placeholder="key (e.g. Color)" onChange={(e) => setAttr(i, "k", e.target.value)}
                         className="w-40 h-7 px-2 text-[12px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
                  <input value={p.v} placeholder="value (e.g. Red)" onChange={(e) => setAttr(i, "v", e.target.value)}
                         className="flex-1 h-7 px-2 text-[12px] border border-slate-200 rounded-sm focus:ring-1 focus:ring-rose-500 outline-none" />
                  <button onClick={() => delAttr(i)} className="text-slate-400 hover:text-red-600"><X size={12} /></button>
                </div>
              ))}
              {attrs.length === 0 && <div className="text-[11px] text-slate-400 italic px-2">e.g. Color=Red, Size=Small</div>}
            </div>
          </div>
        </div>
        <div className="h-11 border-t border-slate-200 flex items-center justify-end gap-2 px-4">
          <button onClick={onClose} className="text-[12px] text-slate-600 hover:text-slate-900 px-3 h-8">Cancel</button>
          <Button variant="primary" onClick={submit} disabled={busy} testId="1688-variant-save">
            {busy ? <><RefreshCw size={12} className="animate-spin" /> Saving…</> : (variant ? "Save changes" : "Add variant")}
          </Button>
        </div>
      </div>
    </div>
  );
}


function ExportTab({ detail, rid }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/1688/${rid}/convert/${m}`);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  return (
    <div className="p-4">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Preview marketplace template</div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {MARKETPLACES.map((m) => (
          <button key={m} onClick={() => load(m)} data-testid={`1688-preview-${m}`}
                  className="h-7 px-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-sm text-[11.5px] font-semibold capitalize">
            {m}
          </button>
        ))}
        {loading && <RefreshCw size={12} className="animate-spin text-slate-500" />}
      </div>
      {preview && (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <div className="h-9 border-b border-slate-200 bg-slate-50 flex items-center justify-between px-3">
            <div className="text-[12px] font-semibold text-slate-800 capitalize">{preview.marketplace} template</div>
            <div className="text-[11px] text-slate-500 font-mono">{preview.row_count} row(s)</div>
          </div>
          <div className="max-h-[calc(100vh-360px)] overflow-auto">
            <table className="erp-table w-full">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="text-left">Column</th>
                  {preview.rows.map((_, i) => <th key={i} className="text-left">Row {i + 1}</th>)}
                </tr>
              </thead>
              <tbody>
                {Array.from(new Set(preview.rows.flatMap((r) => Object.keys(r)))).map((col) => (
                  <tr key={col}>
                    <td className="font-mono text-[11px] text-slate-600">{col}</td>
                    {preview.rows.map((r, i) => (
                      <td key={i} className="text-[11.5px] max-w-[320px] truncate" title={String(r[col] ?? "")}>{String(r[col] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ------- Small helpers ----------------------------------------------------

function Field({ label, children, testId, hint }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10.5px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
              className="w-full h-7 text-[12px] border border-slate-200 rounded-sm px-1.5 focus:ring-1 focus:ring-blue-500 outline-none bg-white">
        {options.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
      </select>
    </div>
  );
}

function FilterInput({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type} data-testid={testId}
             className="w-full h-7 text-[12px] border border-slate-200 rounded-sm px-1.5 focus:ring-1 focus:ring-blue-500 outline-none bg-white" />
    </div>
  );
}

function FilterRange({ label, min, max, onChangeMin, onChangeMax, testId, type = "number", hint }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5" title={hint || ""}>{label} (range)</div>
      <div className="flex items-center gap-1">
        <input type={type} step={type === "number" ? "0.01" : undefined} value={min}
               onChange={(e) => onChangeMin(e.target.value)}
               placeholder={type === "number" ? "min" : ""} data-testid={`${testId}-min`}
               className="w-1/2 h-7 text-[12px] border border-slate-200 rounded-sm px-1.5 focus:ring-1 focus:ring-blue-500 outline-none bg-white font-mono" />
        <span className="text-[10px] text-slate-400">–</span>
        <input type={type} step={type === "number" ? "0.01" : undefined} value={max}
               onChange={(e) => onChangeMax(e.target.value)}
               placeholder={type === "number" ? "max" : ""} data-testid={`${testId}-max`}
               className="w-1/2 h-7 text-[12px] border border-slate-200 rounded-sm px-1.5 focus:ring-1 focus:ring-blue-500 outline-none bg-white font-mono" />
      </div>
    </div>
  );
}

function FilterUserSelect({ label, value, onChange, users = [], testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
              className="w-full h-7 text-[12px] border border-slate-200 rounded-sm px-1.5 focus:ring-1 focus:ring-blue-500 outline-none bg-white">
        <option value="">Any</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>{u.name || u.email}</option>
        ))}
      </select>
    </div>
  );
}

function KnobInput({ label, k, v, onChange, suffix, disabled }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="relative">
        <input type="number" step="0.01" value={v} onChange={(e) => onChange(e.target.value)}
               disabled={disabled} data-testid={`1688-knob-${k}`}
               className="w-full h-8 text-[13px] border border-slate-200 rounded-sm pl-2 pr-8 font-mono focus:ring-1 focus:ring-rose-500 outline-none" />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-500 font-mono">{suffix}</span>}
      </div>
    </div>
  );
}
